import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, LogOut, User, Search } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { cn } from '../../lib/utils';
import { useSocketStore } from '../../stores/socket.store';
import Avatar from '../ui/Avatar';

const PAGE_TITLES: Record<string, string> = {
  '/conversations': 'Conversations',
  '/tickets': 'Tickets',
  '/customers': 'Customers',
  '/team-chat': 'Team Chat',
  '/knowledge-base': 'Knowledge Base',
  '/flows': 'Flows',
  '/marketplace': 'Marketplace',
  '/analytics': 'Analytics',
  '/coaching': 'Coaching',
  '/quality': 'Quality Review',
  '/settings': 'Settings',
  '/dashboard': 'Dashboard',
  '/admin': 'Platform Admin',
};

function getPageTitle(pathname: string): string {
  // Try exact match first, then prefix match
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const match = Object.keys(PAGE_TITLES)
    .sort((a, b) => b.length - a.length)
    .find((k) => pathname.startsWith(k));
  return match ? PAGE_TITLES[match] : 'Dashboard';
}

export default function TopBar() {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { connected, unreadCount, markAllRead } = useSocketStore();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const title = getPageTitle(location.pathname);

  // Close menus on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header
      className="h-14 flex items-center justify-between px-5 border-b border-border bg-surface shrink-0"
    >
      {/* Left: page title */}
      <h1 className="font-heading font-semibold text-text text-lg truncate">
        {title}
      </h1>

      {/* Right: controls */}
      <div className="flex items-center gap-2">
        {/* Global search trigger */}
        <button
          onClick={() => document.dispatchEvent(new CustomEvent('open-search'))}
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-text-3 bg-surface-2 border border-border rounded-lg hover:bg-surface-3 transition-colors"
          aria-label="Search"
        >
          <Search size={13} />
          <span className="text-xs">Search</span>
          <kbd className="text-[10px] px-1 py-0.5 bg-surface border border-border rounded">⌘K</kbd>
        </button>

        {/* Socket status dot */}
        <div
          className="flex items-center gap-1.5"
          title={connected ? 'Connected' : 'Disconnected'}
        >
          <span
            className={cn(
              'w-2 h-2 rounded-full',
              connected ? 'bg-accent-2 shadow-sm shadow-accent-2/50' : 'bg-text-3',
            )}
          />
        </div>

        {/* Notification bell */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => {
              setNotifOpen((o) => !o);
              if (!notifOpen) markAllRead();
            }}
            className="relative p-2 rounded-lg text-text-3 hover:text-text hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label="Notifications"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-accent-3 text-white text-[9px] font-bold px-0.5">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-72 bg-surface border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium text-text">Notifications</p>
              </div>
              <div className="py-6 text-center">
                <p className="text-sm text-text-3">No new notifications</p>
              </div>
            </div>
          )}
        </div>

        {/* User avatar + dropdown */}
        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen((o) => !o)}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label="User menu"
          >
            <Avatar
              src={user?.imageUrl}
              name={user?.fullName ?? user?.username ?? user?.primaryEmailAddress?.emailAddress}
              size="sm"
            />
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-52 bg-surface border border-border rounded-xl shadow-2xl z-50 p-1">
              {/* User info */}
              <div className="px-3 py-2.5 border-b border-border mb-1">
                <p className="text-sm font-medium text-text truncate">
                  {user?.fullName ?? 'Agent'}
                </p>
                <p className="text-xs text-text-3 truncate">
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
              </div>
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-2 hover:bg-surface-2 hover:text-text rounded-lg transition-colors"
                onClick={() => {
                  setUserMenuOpen(false);
                  // navigate to profile — use Link would require hooks, keep simple
                  window.location.href = '/settings/profile';
                }}
              >
                <User size={14} />
                Profile
              </button>
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-accent-3 hover:bg-accent-3/10 rounded-lg transition-colors"
                onClick={() => signOut()}
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
